/* ===========================================================
 * ANB Baby - cart.js
 *
 * Two responsibilities:
 *
 * 1. <cart-items> custom element (main /cart page only)
 *    Handles the main cart page checkout button, gift card UI,
 *    cart countdown, and terms checkbox.
 *
 * 2. Global slideout cart checkout delegation
 *    Runs on every page since the slideout cart appears site-wide.
 *    Catches clicks on #cart-sidebar-checkout regardless of when
 *    the slideout was rendered or re-rendered via AJAX.
 *
 * Both pre-checkout paths apply discount codes and cart notes
 * to the cart before redirecting to /checkout, so users don't
 * lose their input on the way to checkout.
 *
 * IMPORTANT: This file must be loaded globally via theme.liquid,
 * not just on the cart template, because the slideout cart
 * exists on every page.
 *
 * NOTE: Free shipping message HTML rendering bug was fixed at the
 * source in assets/free-shipping-component.js (changed textContent
 * to innerHTML, removed 400ms setTimeout). If a Halo theme update
 * overwrites that file, the bug returns and you'll see escaped
 * HTML entities in the cart drawer free shipping message.
 * =========================================================== */

(function () {
  'use strict';

  /* -----------------------------------------------------------
   * Shared helpers
   * ----------------------------------------------------------- */

  function getCheckoutHref(button) {
    const root = (window.routes && window.routes.root) || '';
    return (
      button?.dataset?.href ||
      (window.routes && window.routes.checkout) ||
      `${root}/checkout`
    );
  }

  function getCartUpdateUrl() {
    return (
      (window.routes && (window.routes.cart_update_url || window.routes.cartUpdate)) ||
      '/cart/update.js'
    );
  }

  function getFetchConfig() {
    if (typeof window.fetchConfig === 'function') {
      return window.fetchConfig();
    }
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };
  }

  /**
   * Check if cart contains excluded brands (Nuna, Silver Cross)
   */
  function cartHasExcludedBrands() {
    const excludedBrands = ['Nuna', 'Silver Cross'];
    const items = document.querySelectorAll('[data-item-row]');
    
    for (const item of items) {
      const vendorElement = item.querySelector('.cart-item-vendor');
      if (vendorElement) {
        const vendor = vendorElement.textContent.trim();
        if (excludedBrands.includes(vendor)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Show error message for coupon code
   */
  function showCouponError(couponInput) {
    let errorDiv = couponInput?.parentElement?.querySelector('.anb-coupon-error');
    
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'anb-coupon-error';
      errorDiv.style.cssText = `
        color: #cc1f4a;
        font-size: 13px;
        margin-top: 8px;
        display: block;
      `;
      couponInput?.parentElement?.appendChild(errorDiv);
    }
    
    errorDiv.textContent = 'Coupon codes cannot be applied to orders containing excluded brands.';
    errorDiv.style.display = 'block';
  }

  /**
   * Clear coupon error message
   */
  function clearCouponError(couponInput) {
    const errorDiv = couponInput?.parentElement?.querySelector('.anb-coupon-error');
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }
  }

  /**
   * Apply a discount code by hitting Shopify's /discount/{code} endpoint.
   * Sets the discount cookie so it persists into checkout.
   */
  async function applyDiscountCode(code) {
    if (!code) return;
    try {
      localStorage.setItem('storedDiscount', code);
      const res = await fetch(`/discount/${encodeURIComponent(code)}`, {
        credentials: 'include',
      });
      // Force read so Safari completes the request before we navigate
      await res.text();
    } catch (err) {
      console.error('ANB cart: discount apply failed:', err?.message || err);
    }
  }

  /**
   * Save a cart note via /cart/update.js.
   */
  async function saveCartNote(note) {
    if (!note) return;
    try {
      const body = JSON.stringify({ note });
      const res = await fetch(getCartUpdateUrl(), {
        ...getFetchConfig(),
        body,
      });
      await res.text();
    } catch (err) {
      console.error('ANB cart: note save failed:', err?.message || err);
    }
  }

  /**
   * Run the pre-checkout flow (discount + note), then redirect.
   * Wrapped so any single failure doesn't block the user from
   * getting to /checkout. The point is to never strand a user
   * on the cart drawer.
   */
  async function runPreCheckoutAndRedirect(button, couponInput, noteInput) {
    const checkoutHref = getCheckoutHref(button);

    try {
      const couponCode = couponInput?.value?.trim();
      if (couponCode) await applyDiscountCode(couponCode);

      const cartNote = noteInput?.value?.trim();
      if (cartNote) await saveCartNote(cartNote);
    } catch (err) {
      console.error('ANB cart pre-checkout error:', err?.message || err);
    }

    window.location.assign(checkoutHref);
  }

  /* -----------------------------------------------------------
   * Global delegation - slideout cart checkout button
   *
   * Runs once at script load, regardless of whether <cart-items>
   * is on the page. The slideout cart contents get re-rendered
   * via AJAX on every cart change, so direct event binding
   * doesn't survive. Delegation on document does.
   * ----------------------------------------------------------- */

  if (!window.__anbSidebarCheckoutDelegated) {
    window.__anbSidebarCheckoutDelegated = true;

    document.addEventListener('click', async function (e) {
      const sidebarBtn = e.target.closest('#cart-sidebar-checkout');
      if (!sidebarBtn) return;

      // Don't redirect if the button is in a disabled state
      // (terms checkbox not yet checked, etc.)
      if (sidebarBtn.disabled || sidebarBtn.hasAttribute('disabled')) {
        return;
      }

      e.preventDefault();

      // Slideout uses its own input IDs (separate from main cart page)
      const couponInput = document.getElementById('coupon-code');
      const noteInput = document.querySelector('#popup-tool-note textarea[name="note"]');

      await runPreCheckoutAndRedirect(sidebarBtn, couponInput, noteInput);
    });
  }

  /* -----------------------------------------------------------
   * Global delegation - coupon code submit (slideout + main cart)
   * Validates before applying discount
   * ----------------------------------------------------------- */

  if (!window.__anbCouponValidationDelegated) {
    window.__anbCouponValidationDelegated = true;

    document.addEventListener('click', async function (e) {
      const submitBtn = e.target.closest('[data-update-coupon]');
      if (!submitBtn) return;

      e.preventDefault();

      // Find the coupon input in the same parent/modal
      const couponInput = submitBtn.closest('.discount-code-field, .cart-coupon-code')?.querySelector('input[name="discount"]');
      if (!couponInput || !couponInput.value.trim()) return;

      // Check if cart has excluded brands
      if (cartHasExcludedBrands()) {
        showCouponError(couponInput);
        couponInput.value = '';
        return;
      }

      clearCouponError(couponInput);
      // Proceed with discount application (existing logic)
      // The coupon will be applied via the form submission or fetch
    });
  }

  /* -----------------------------------------------------------
   * <cart-items> custom element - main /cart page
   * ----------------------------------------------------------- */

  class CartItems extends HTMLElement {
    constructor() {
      super();

      // Elements
      this.cartCountDown = document.getElementById(`CartCountdown-${this.dataset.section}`);
      this.giftCardElement = document.getElementById('is-a-gift');
      this.giftCardButton = document.getElementById('cart-gift-wrapping');
      this.toCheckoutButton = document.getElementById('cart-checkout');
      this.couponCodeInput = document.getElementById('cart-coupon-code');
      this.cartNoteInput = document.getElementById('cart-note');
      this.checkTerms = document.getElementById('cart_conditions');

      // Bind handlers ONCE so removeEventListener works
      this.handleToCheckoutPage = this.handleToCheckoutPage.bind(this);
      this.onAddGiftCardClick = this.onAddGiftCardClick.bind(this);
      this.onTermsChange = this.onTermsChange.bind(this);

      // Listener for main cart page checkout button
      this.toCheckoutButton?.addEventListener('click', this.handleToCheckoutPage);

      // Init features
      this.initToCheckoutButtonDisabling();
      this.initCartCountdown();
      if (this.giftCardElement) this.initGiftCardElement();
      this.initGiftCardManipulation();
    }

    /* -----------------------------
     * Main cart page checkout
     * ----------------------------- */
    async handleToCheckoutPage(e) {
      e.preventDefault();
      await runPreCheckoutAndRedirect(
        this.toCheckoutButton,
        this.couponCodeInput,
        this.cartNoteInput
      );
    }

    /* -----------------------------
     * Gift card UI
     * ----------------------------- */
    initGiftCardElement() {
      const isChecked = this.giftCardButton?.dataset.isChecked;
      if (isChecked === 'true') {
        const variantId = this.giftCardButton?.dataset.giftId;
        if (variantId) {
          const giftCardRemoveButton = document.querySelector(`[data-cart-remove-id="${variantId}"]`);
          const giftCardQuantityInput = document.querySelector(`[data-cart-quantity-id="${variantId}"]`);

          this.giftCardElement.style.display = 'none';

          giftCardRemoveButton?.addEventListener('click', () => {
            this.giftCardButton.dataset.isChecked = 'false';
            this.giftCardElement.style.display = 'flex';
          });

          giftCardQuantityInput?.addEventListener('change', (e) => {
            const value = Number(e.target.value);
            if (value <= 0) {
              this.giftCardButton.dataset.isChecked = 'false';
              this.giftCardElement.style.display = 'flex';
            }
          });
        }
      } else {
        this.giftCardElement.style.display = 'flex';
      }
    }

    initGiftCardManipulation() {
      if (this.giftCardButton) {
        this.giftCardButton.removeEventListener('click', this.onAddGiftCardClick);
        this.giftCardButton.addEventListener('click', this.onAddGiftCardClick);
      }
    }

    onAddGiftCardClick(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!this.giftCardButton || !this.giftCardElement) return;
      this.giftCardButton.dataset.isChecked = 'true';
      this.giftCardElement.style.display = 'none';
    }

    /* -----------------------------
     * Terms checkbox - enable/disable checkout
     * ----------------------------- */
    initToCheckoutButtonDisabling() {
      if (!this.checkTerms || !this.toCheckoutButton) return;
      this.onTermsChange();
      this.checkTerms.addEventListener('change', this.onTermsChange);
      window.addEventListener('load', this.onTermsChange);
    }

    onTermsChange() {
      if (!this.toCheckoutButton) return;
      this.toCheckoutButton.disabled = !!this.checkTerms && !this.checkTerms.checked;
    }

    /* -----------------------------
     * Cart countdown timer
     * ----------------------------- */
    initCartCountdown() {
      if (!this.cartCountDown) return;
      if (this.cartCountDown.classList.contains('is-running')) return;

      const minsAttr = Number(this.cartCountDown.getAttribute('data-cart-countdown')) || 0;
      const duration = Math.max(0, minsAttr) * 60;
      const element = this.cartCountDown.querySelector('.time');

      this.cartCountDown.setAttribute('role', 'status');
      this.cartCountDown.setAttribute('aria-live', 'polite');
      this.cartCountDown.setAttribute('aria-atomic', 'true');

      this.cartCountDown.classList.add('is-running');
      this.startTimerCartCountdown(duration, element);
    }

    startTimerCartCountdown(duration, element) {
      let timer = duration;
      const intervalId = setInterval(() => {
        const minutes = String(Math.floor(timer / 60)).padStart(2, '0');
        const seconds = String(Math.floor(timer % 60)).padStart(2, '0');
        if (element) element.innerText = `${minutes}:${seconds}`;
        timer -= 1;
        if (timer < 0) {
          clearInterval(intervalId);
          this.cartCountDown?.remove();
        }
      }, 1000);
    }
  }

  if (!customElements.get('cart-items')) {
    customElements.define('cart-items', CartItems);
  }

  /* -----------------------------------------------------------
   * <accordion-block> custom element
   * ----------------------------------------------------------- */

  class Accordion extends HTMLElement {
    constructor() {
      super();
      this.header = this.firstElementChild;
      this.content = this.lastElementChild;

      if (!this.header || !this.content) return;

      // ARIA baseline
      this.setAttribute('role', 'group');
      this.header.setAttribute('role', 'button');
      this.header.setAttribute('tabindex', '0');
      const open = this.hasAttribute('open');
      this.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.content.setAttribute('aria-hidden', open ? 'false' : 'true');

      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.header.addEventListener('click', this.onSummaryClick);
      this.header.addEventListener('keydown', this.onKeydown);
    }

    onKeydown(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.onSummaryClick(e);
      }
    }

    onSummaryClick(e) {
      e.preventDefault();
      this.hasAttribute('open') ? this.close() : this.open();
    }

    open() {
      this.setAttribute('open', '');
      this.setAttribute('aria-expanded', 'true');
      this.content.setAttribute('aria-hidden', 'false');
      if (window.jQuery) {
        window.jQuery(this.content).stop(true, true).slideDown();
      } else {
        this.content.style.display = 'block';
      }
    }

    close() {
      this.removeAttribute('open');
      this.setAttribute('aria-expanded', 'false');
      this.content.setAttribute('aria-hidden', 'true');
      if (window.jQuery) {
        window.jQuery(this.content).stop(true, true).slideUp();
      } else {
        this.content.style.display = 'none';
      }
    }
  }

  if (!customElements.get('accordion-block')) {
    customElements.define('accordion-block', Accordion);
  }
})();